import type { NetworkNode } from "../types";

export interface NodePosition {
  id: string;
  x: number;
  y: number;
}

const PAD = 8;

/**
 * Deterministic layered layout for the network map. Nodes are placed in columns
 * by their breadth-first depth from a source-like root (fewest incoming links,
 * ties by array order); within a column they spread by array order. Coordinates
 * are in 0..100 (percent of the viewBox). A pure function of (id, connections).
 */
export function layoutNodes(nodes: readonly NetworkNode[]): NodePosition[] {
  const ids = nodes.map((n) => n.id);
  if (ids.length === 0) {
    return [];
  }
  const orderIndex = new Map<string, number>(ids.map((id, i) => [id, i]));
  const has = new Set(ids);
  const byOrder = (a: string, b: string): number =>
    (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0);

  const adj = new Map<string, Set<string>>(ids.map((id) => [id, new Set<string>()]));
  const incoming = new Map<string, number>(ids.map((id) => [id, 0]));
  for (const n of nodes) {
    for (const c of n.connections) {
      if (has.has(c)) {
        adj.get(n.id)?.add(c);
        adj.get(c)?.add(n.id);
        incoming.set(c, (incoming.get(c) ?? 0) + 1);
      }
    }
  }

  const layerOf = new Map<string, number>();
  const visited = new Set<string>();
  while (visited.size < ids.length) {
    const remaining = ids.filter((id) => !visited.has(id));
    remaining.sort((a, b) => (incoming.get(a) ?? 0) - (incoming.get(b) ?? 0) || byOrder(a, b));
    const root = remaining[0];
    if (root === undefined) {
      break;
    }
    const queue: Array<{ id: string; layer: number }> = [{ id: root, layer: 0 }];
    visited.add(root);
    layerOf.set(root, 0);
    while (queue.length > 0) {
      const current = queue.shift();
      if (current === undefined) {
        break;
      }
      const neighbors = [...(adj.get(current.id) ?? [])].sort(byOrder);
      for (const nb of neighbors) {
        if (!visited.has(nb)) {
          visited.add(nb);
          layerOf.set(nb, current.layer + 1);
          queue.push({ id: nb, layer: current.layer + 1 });
        }
      }
    }
  }

  const maxLayer = Math.max(0, ...layerOf.values());
  const columns = new Map<number, string[]>();
  for (const id of ids) {
    const layer = layerOf.get(id) ?? 0;
    const col = columns.get(layer) ?? [];
    col.push(id);
    columns.set(layer, col);
  }
  for (const col of columns.values()) {
    col.sort(byOrder);
  }

  const span = 100 - 2 * PAD;
  return ids.map((id) => {
    const layer = layerOf.get(id) ?? 0;
    const col = columns.get(layer) ?? [id];
    const row = col.indexOf(id);
    const x = maxLayer === 0 ? 50 : PAD + (layer / maxLayer) * span;
    const y = col.length <= 1 ? 50 : PAD + (row / (col.length - 1)) * span;
    return { id, x, y };
  });
}
